import React, { useState, useEffect } from 'react';
import API from '../../utils/api';
import './StudentsView.css';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

function StudentsView({ branchId }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [viewStudent, setViewStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [branches, setBranches] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [docPreviews, setDocPreviews] = useState({});
  
  const role = localStorage.getItem('role');
  const branchName = localStorage.getItem('branchName') || localStorage.getItem('branch') || '';

  const initialFormState = {
    branchId: branchId || '',
    name: '', fatherName: '', motherName: '', guardianPhone: '', dob: '', gender: '',
    course: '', duration: '', batchTiming: '', branch: branchName, modeOfTraining: '', preferredBatchTime: '',
    email: '', phone: '', aadharNumber: '', education: '', panNumber: '', password: '',
    feeAmount: '', paidAmount: '',
    address: { street: '', city: '', state: '', pincode: '' },
    educationDetails: [
      { qualification: '10th', board: '', year: '', percentage: '' },
      { qualification: '12th', board: '', year: '', percentage: '' },
      { qualification: 'Graduation (UG)', board: '', year: '', percentage: '' },
      { qualification: 'Post Graduation (PG)', board: '', year: '', percentage: '' },
      { qualification: 'Other', board: '', year: '', percentage: '' }
    ],
    workExperience: { isWorking: false, companyName: '', designation: '', experienceYears: '' },
    documents: {
      aadharCopy: null, panCopy: null, photos: null,
      marksheet10: null, marksheet12: null, marksheetUG: null, marksheetPG: null
    },
    installmentPlan: {
      planType: 'One Time',
      installments: []
    },
    appliedCoupon: '',
    discountAmount: 0
  };
  const [formData, setFormData] = useState(initialFormState);
  
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');

  useEffect(() => {
    fetchStudents();
    if (role === 'Super Admin' && !branchId) {
      fetchBranches();
    }
  }, [branchId]);

  const fetchBranches = async () => {
    try {
      const res = await API.get('/api/admin/branches');
      setBranches(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (formData.branchId || role !== 'Super Admin') {
      fetchCoursesForBranch(formData.branchId);
    } else {
      setAvailableCourses([]);
    }
  }, [formData.branchId, role]);

  const fetchCoursesForBranch = async (bId) => {
    try {
      const url = bId ? `/api/admin/courses?branchId=${bId}` : '/api/admin/courses';
      const res = await API.get(url);
      setAvailableCourses(res.data.filter(c => c.status === 'Active'));
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const url = branchId ? `/api/admin/students?branchId=${branchId}` : '/api/admin/students';
      const res = await API.get(url);
      setStudents(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      setLoading(false);
    }
  };

  const handleEditClick = (student) => {
    setEditMode(true);
    setEditingStudentId(student._id);
    setFormData({
      branchId: student.branchId || branchId || '',
      name: student.name || '',
      fatherName: student.fatherName || '',
      motherName: student.motherName || '',
      guardianPhone: student.guardianPhone || '',
      dob: student.dob || '',
      gender: student.gender || '',
      course: student.course || '',
      duration: student.duration || '',
      batchTiming: student.batchTiming || '',
      branch: student.branch || '',
      modeOfTraining: student.modeOfTraining || '',
      preferredBatchTime: student.preferredBatchTime || '',
      email: student.email || '',
      phone: student.phone || '',
      aadharNumber: student.aadharNumber || '',
      education: student.education || '',
      panNumber: student.panNumber || '',
      password: '', // Don't populate password for security, leave blank unless changing
      feeAmount: student.feeAmount || '',
      paidAmount: student.paidAmount || '',
      address: student.address || { street: '', city: '', state: '', pincode: '' },
      educationDetails: student.educationDetails?.length ? student.educationDetails : initialFormState.educationDetails,
      workExperience: student.workExperience || initialFormState.workExperience,
      documents: student.documents || initialFormState.documents,
      installmentPlan: student.installmentPlan || initialFormState.installmentPlan
    });
    setProfileImage(null);
    if (student.profileImage) {
      setProfileImagePreview(`${BASE_URL}${student.profileImage}`);
    } else {
      setProfileImagePreview(null);
    }
    setDocPreviews({});
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCourseChange = (e) => {
    const selectedCourseName = e.target.value;
    const selectedCourseObj = availableCourses.find(c => c.courseName === selectedCourseName);
    
    let updatedFeeAmount = formData.feeAmount;
    let updatedInstallments = formData.installmentPlan.installments;

    if (selectedCourseObj) {
      updatedFeeAmount = selectedCourseObj.feeAmount;
      if (formData.installmentPlan.planType === 'One Time') {
        updatedInstallments = [{ amount: updatedFeeAmount, dueDate: new Date().toISOString().split('T')[0] }];
      }
    }

    setFormData({
      ...formData,
      course: selectedCourseName,
      feeAmount: updatedFeeAmount,
      appliedCoupon: '',
      discountAmount: 0,
      installmentPlan: {
        ...formData.installmentPlan,
        installments: updatedInstallments
      }
    });
    setCouponInput('');
    setCouponSuccess('');
    setCouponError('');
  };

  const handleNestedChange = (section, field, value) => {
    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value
      }
    });
  };

  const handleEducationChange = (index, field, value) => {
    const updatedEdu = [...formData.educationDetails];
    updatedEdu[index][field] = value;
    setFormData({ ...formData, educationDetails: updatedEdu });
  };

  const handleInstallmentPlanChange = (e) => {
    const planType = e.target.value;
    const totalFee = Number(formData.feeAmount) || 0;
    
    let newInstallments = [];
    if (planType === 'One Time' && totalFee > 0) {
      newInstallments = [{ amount: totalFee, dueDate: new Date().toISOString().split('T')[0] }];
    } else if (planType === 'Custom') {
      newInstallments = [{ amount: '', dueDate: '' }];
    }
    
    setFormData({
      ...formData,
      installmentPlan: {
        planType,
        installments: newInstallments
      }
    });
  };

  const handleAddInstallment = () => {
    setFormData({
      ...formData,
      installmentPlan: {
        ...formData.installmentPlan,
        installments: [...formData.installmentPlan.installments, { amount: '', dueDate: '' }]
      }
    });
  };

  const handleRemoveInstallment = (index) => {
    const updated = [...formData.installmentPlan.installments];
    updated.splice(index, 1);
    setFormData({
      ...formData,
      installmentPlan: {
        ...formData.installmentPlan,
        installments: updated
      }
    });
  };

  const handleInstallmentChange = (index, field, value) => {
    const updated = [...formData.installmentPlan.installments];
    updated[index][field] = value;
    setFormData({
      ...formData,
      installmentPlan: {
        ...formData.installmentPlan,
        installments: updated
      }
    });
  };

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    try {
      const res = await API.get(`/api/admin/coupons/validate?code=${couponInput}`);
      const coupon = res.data.coupon;
      
      let discount = 0;
      let baseFee = Number(formData.feeAmount) + Number(formData.discountAmount || 0); // Get original fee before any previous discounts
      
      if (coupon.discountType === 'Percentage') {
        discount = (baseFee * coupon.discountValue) / 100;
      } else {
        discount = coupon.discountValue;
      }
      
      // Ensure discount doesn't exceed base fee
      if (discount > baseFee) discount = baseFee;

      const newTotal = baseFee - discount;
      
      let updatedInstallments = formData.installmentPlan.installments;
      if (formData.installmentPlan.planType === 'One Time') {
        updatedInstallments = [{ amount: newTotal, dueDate: new Date().toISOString().split('T')[0] }];
      }
      
      setFormData(prev => ({
        ...prev,
        feeAmount: newTotal,
        appliedCoupon: coupon.code,
        discountAmount: discount,
        installmentPlan: {
          ...prev.installmentPlan,
          installments: updatedInstallments
        }
      }));
      
      setCouponSuccess(`Coupon applied! You saved ₹${discount}`);
      setCouponError('');
    } catch (err) {
      setCouponError(err.response?.data?.message || 'Invalid coupon');
      setCouponSuccess('');
    }
  };

  const handleRemoveCoupon = () => {
    const baseFee = Number(formData.feeAmount) + Number(formData.discountAmount || 0);
    
    let updatedInstallments = formData.installmentPlan.installments;
    if (formData.installmentPlan.planType === 'One Time') {
      updatedInstallments = [{ amount: baseFee, dueDate: new Date().toISOString().split('T')[0] }];
    }

    setFormData(prev => ({
      ...prev,
      feeAmount: baseFee,
      appliedCoupon: '',
      discountAmount: 0,
      installmentPlan: {
        ...prev.installmentPlan,
        installments: updatedInstallments
      }
    }));
    
    setCouponInput('');
    setCouponSuccess('');
    setCouponError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (branchId) payload.branchId = branchId;
      
      const submitData = new FormData();
      // Remove File objects from JSON payload so we don't send empty objects
      const payloadWithoutFiles = { ...payload, documents: {} };
      submitData.append('data', JSON.stringify(payloadWithoutFiles));
      
      if (profileImage) submitData.append('profileImage', profileImage);
      if (formData.documents.aadharCopy) submitData.append('aadharCopy', formData.documents.aadharCopy);
      if (formData.documents.panCopy) submitData.append('panCopy', formData.documents.panCopy);
      if (formData.documents.photos) submitData.append('photos', formData.documents.photos);
      if (formData.documents.marksheet10) submitData.append('marksheet10', formData.documents.marksheet10);
      if (formData.documents.marksheet12) submitData.append('marksheet12', formData.documents.marksheet12);
      if (formData.documents.marksheetUG) submitData.append('marksheetUG', formData.documents.marksheetUG);
      if (formData.documents.marksheetPG) submitData.append('marksheetPG', formData.documents.marksheetPG);

      if (editMode) {
        const res = await API.put(`/api/admin/students/${editingStudentId}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (res.data.success) {
          alert('Student updated successfully!');
          setShowModal(false);
          setFormData(initialFormState);
          setProfileImage(null);
          setProfileImagePreview(null);
          setDocPreviews({});
          setEditMode(false);
          setEditingStudentId(null);
          fetchStudents(); // Refresh the list
        }
      } else {
        const res = await API.post('/api/admin/students', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (res.data.success) {
          alert('Student added successfully!');
          setShowModal(false);
          setFormData(initialFormState);
          setProfileImage(null);
          setProfileImagePreview(null);
          setDocPreviews({});
          fetchStudents(); // Refresh the list
        }
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to process request');
    }
  };

  const filteredStudents = students.filter(student => 
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.course?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.branch?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
        <h2>Manage Students</h2>
        
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <input 
            type="text" 
            placeholder="Search students..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #ccc', minWidth: '250px' }}
          />
          <button className="add-btn" onClick={() => {
            setEditMode(false);
            setEditingStudentId(null);
            setFormData(initialFormState);
            setProfileImage(null);
            setProfileImagePreview(null);
            setDocPreviews({});
            setShowModal(true);
          }}>+ Add Student</button>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <span className="close-btn" onClick={() => setShowModal(false)}>&times;</span>

            {/* ── Registration Form Header with Logo ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #0284c7', paddingBottom: '14px', marginBottom: '22px' }}>
              <div>
                <h3 style={{ color: '#0284c7', margin: 0, fontSize: '20px', fontWeight: 800, letterSpacing: '0.5px' }}>{editMode ? 'EDIT STUDENT' : 'STUDENT REGISTRATION FORM'}</h3>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>Fill all required fields carefully</p>
              </div>
              <img src="/webmok-logo.png" alt="WebMok" style={{ height: '50px', objectFit: 'contain' }}
                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
            </div>

            <form onSubmit={handleSubmit} className="student-form-advanced">

              {/* ── 1. PERSONAL DETAILS ── */}
              <div className="form-section">
                <h4>1. PERSONAL DETAILS</h4>
                <div className="form-grid">
                  <div className="input-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Profile Image</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                      <div style={{
                        width: '110px', height: '110px', borderRadius: '10px',
                        border: '2px dashed #0284c7', overflow: 'hidden',
                        background: '#f0f9ff', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', flexShrink: 0
                      }}>
                        {profileImagePreview
                          ? <img src={profileImagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center', padding: '8px' }}>No Image</span>
                        }
                      </div>
                      <div>
                        <input type="file" accept="image/*" onChange={(e) => {
                          const file = e.target.files[0];
                          setProfileImage(file);
                          if (file) setProfileImagePreview(URL.createObjectURL(file));
                          else setProfileImagePreview(null);
                        }} />
                        {profileImagePreview && (
                          <p style={{ fontSize: '11px', color: '#10b981', marginTop: '6px' }}>✓ Image selected</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="input-group">
                    <label>Full Name (As per ID Proof) *</label>
                    <input type="text" name="name" required value={formData.name} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Date of Birth</label>
                    <input type="date" name="dob" value={formData.dob} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Father's / Guardian Name</label>
                    <input type="text" name="fatherName" value={formData.fatherName} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Mother's Name</label>
                    <input type="text" name="motherName" value={formData.motherName} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Guardian Phone</label>
                    <input type="text" name="guardianPhone" value={formData.guardianPhone} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Gender</label>
                    <select name="gender" value={formData.gender} onChange={handleInputChange}>
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="input-group">
                    <label>Mobile Number</label>
                    <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Email ID *</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Student Login Password</label>
                    <input type="text" name="password" placeholder={editMode ? "Leave blank to keep unchanged" : "Set a password"} value={formData.password} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Aadhar Card Number</label>
                    <input type="text" name="aadharNumber" value={formData.aadharNumber} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Education / Qualification</label>
                    <input type="text" name="education" value={formData.education} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>PAN Card Number</label>
                    <input type="text" name="panNumber" value={formData.panNumber} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Street Address</label>
                    <input type="text" placeholder="Street / Area" value={formData.address.street} onChange={(e) => handleNestedChange('address', 'street', e.target.value)} />
                  </div>
                  <div className="input-group">
                    <label>City</label>
                    <input type="text" value={formData.address.city} onChange={(e) => handleNestedChange('address', 'city', e.target.value)} />
                  </div>
                  <div className="input-group">
                    <label>State</label>
                    <input type="text" value={formData.address.state} onChange={(e) => handleNestedChange('address', 'state', e.target.value)} />
                  </div>
                  <div className="input-group">
                    <label>Pincode</label>
                    <input type="text" value={formData.address.pincode} onChange={(e) => handleNestedChange('address', 'pincode', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* ── 2. COURSE DETAILS ── */}
              <div className="form-section">
                <h4>2. COURSE DETAILS</h4>
                <div className="form-grid">
                  <div className="input-group">
                    <label>Course Name *</label>
                    <select name="course" required value={formData.course} onChange={handleCourseChange}>
                      <option value="">-- Select Course --</option>
                      {availableCourses.map(c => (
                        <option key={c._id} value={c.courseName}>{c.courseName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="input-group">
                    <label>Course Duration</label>
                    <input type="text" name="duration" placeholder="e.g. 6 Months" value={formData.duration} onChange={handleInputChange} />
                  </div>
                  <div className="input-group">
                    <label>Batch Timing</label>
                    <input type="text" name="batchTiming" placeholder="e.g. 11:00 AM - 12:00 PM" value={formData.batchTiming} onChange={handleInputChange} />
                  </div>
                  {role === 'Super Admin' && !branchId ? (
                    <div className="input-group">
                      <label>Select Branch *</label>
                      <select 
                        required 
                        name="branchId" 
                        value={formData.branchId || ''} 
                        onChange={(e) => {
                          const selected = branches.find(b => b._id === e.target.value);
                          setFormData({
                            ...formData,
                            branchId: e.target.value,
                            branch: selected ? selected.branchName : ''
                          });
                        }}
                      >
                        <option value="">-- Select Branch --</option>
                        {branches.map(b => <option key={b._id} value={b._id}>{b.branchName}</option>)}
                      </select>
                    </div>
                  ) : (
                    <div className="input-group">
                      <label>Branch (Auto-filled)</label>
                      <input
                        type="text"
                        name="branch"
                        value={formData.branch}
                        onChange={handleInputChange}
                        style={{ backgroundColor: formData.branch ? '#f0fdf4' : '', fontWeight: formData.branch ? '600' : 'normal' }}
                        placeholder={branchName || 'Branch Name'}
                        readOnly={role !== 'Super Admin'}
                      />
                    </div>
                  )}
                  <div className="input-group">
                    <label>Mode of Training</label>
                    <div className="radio-group">
                      <label><input type="radio" name="modeOfTraining" value="Online" checked={formData.modeOfTraining === 'Online'} onChange={handleInputChange}/> Online</label>
                      <label><input type="radio" name="modeOfTraining" value="Offline" checked={formData.modeOfTraining === 'Offline'} onChange={handleInputChange}/> Offline</label>
                      <label><input type="radio" name="modeOfTraining" value="Hybrid" checked={formData.modeOfTraining === 'Hybrid'} onChange={handleInputChange}/> Hybrid</label>
                    </div>
                  </div>
                  <div className="input-group">
                    <label>Preferred Batch Time</label>
                    <div className="radio-group">
                      <label><input type="radio" name="preferredBatchTime" value="Morning" checked={formData.preferredBatchTime === 'Morning'} onChange={handleInputChange}/> Morning</label>
                      <label><input type="radio" name="preferredBatchTime" value="Afternoon" checked={formData.preferredBatchTime === 'Afternoon'} onChange={handleInputChange}/> Afternoon</label>
                      <label><input type="radio" name="preferredBatchTime" value="Evening" checked={formData.preferredBatchTime === 'Evening'} onChange={handleInputChange}/> Evening</label>
                      <label><input type="radio" name="preferredBatchTime" value="Weekend" checked={formData.preferredBatchTime === 'Weekend'} onChange={handleInputChange}/> Weekend</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>3. EDUCATIONAL DETAILS</h4>
                <div className="table-responsive">
                  <table className="edu-table">
                    <thead>
                      <tr>
                        <th>Qualification</th>
                        <th>Board / University</th>
                        <th>Year of Passing</th>
                        <th>Percentage / CGPA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.educationDetails.map((edu, index) => (
                        <tr key={index}>
                          <td>
                            {edu.qualification === 'Other' || edu.isOther ? (
                              <input type="text" placeholder="Other Qualification" value={edu.qualification === 'Other' ? '' : edu.qualification} onChange={(e) => {
                                handleEducationChange(index, 'qualification', e.target.value);
                                handleEducationChange(index, 'isOther', true);
                              }} />
                            ) : (
                              edu.qualification
                            )}
                          </td>
                          <td><input type="text" value={edu.board} onChange={(e) => handleEducationChange(index, 'board', e.target.value)} /></td>
                          <td><input type="text" value={edu.year} onChange={(e) => handleEducationChange(index, 'year', e.target.value)} /></td>
                          <td><input type="text" value={edu.percentage} onChange={(e) => handleEducationChange(index, 'percentage', e.target.value)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* <div className="form-section">
                <h4>4. WORK EXPERIENCE DETAILS</h4>
                <div className="form-grid">
                  <div className="input-group">
                    <label>Are you currently working?</label>
                    <div className="radio-group">
                      <label><input type="radio" name="isWorking" value="true" checked={formData.workExperience.isWorking === true} onChange={() => handleNestedChange('workExperience', 'isWorking', true)}/> Yes</label>
                      <label><input type="radio" name="isWorking" value="false" checked={formData.workExperience.isWorking === false} onChange={() => handleNestedChange('workExperience', 'isWorking', false)}/> No</label>
                    </div>
                  </div>
                  <div className="input-group">
                    <label>If Yes, Company Name</label>
                    <input type="text" disabled={!formData.workExperience.isWorking} value={formData.workExperience.companyName} onChange={(e) => handleNestedChange('workExperience', 'companyName', e.target.value)} />
                  </div>
                  <div className="input-group">
                    <label>Designation</label>
                    <input type="text" disabled={!formData.workExperience.isWorking} value={formData.workExperience.designation} onChange={(e) => handleNestedChange('workExperience', 'designation', e.target.value)} />
                  </div>
                  <div className="input-group">
                    <label>Work Experience (Years)</label>
                    <input type="text" disabled={!formData.workExperience.isWorking} value={formData.workExperience.experienceYears} onChange={(e) => handleNestedChange('workExperience', 'experienceYears', e.target.value)} />
                  </div>
                </div>
              </div> */}

              <div className="form-row-split">
                <div className="form-section half-width">
                  <h4>5. DOCUMENTS REQUIRED</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { key: 'aadharCopy', label: 'Aadhar Card Copy', isImage: false },
                      { key: 'panCopy', label: 'PAN Card Copy', isImage: false },
                      { key: 'photos', label: 'Passport Size Photographs', isImage: true },
                    ].map(({ key, label, isImage }) => (
                      <div key={key} className="input-group">
                        <label>{label}</label>
                        <input type="file" accept={isImage ? 'image/*' : '*'} onChange={(e) => {
                          const file = e.target.files[0];
                          handleNestedChange('documents', key, file);
                          if (file && isImage) {
                            setDocPreviews(prev => ({ ...prev, [key]: URL.createObjectURL(file) }));
                          } else if (file) {
                            setDocPreviews(prev => ({ ...prev, [key]: file.name }));
                          }
                        }}/>
                        {docPreviews[key] && (
                          isImage
                            ? <img src={docPreviews[key]} alt={label} style={{ marginTop: '6px', width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }} />
                            : <p style={{ fontSize: '11px', color: '#10b981', marginTop: '4px' }}>✓ {docPreviews[key]}</p>
                        )}
                      </div>
                    ))}
                    <p style={{ margin: '4px 0 0', fontWeight: 'bold', fontSize: '12px', color: '#555', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>Educational Documents:</p>
                    {[
                      { key: 'marksheet10', label: '10th Marksheet' },
                      { key: 'marksheet12', label: '12th Marksheet' },
                      { key: 'marksheetUG', label: 'UG Last Semester DMC' },
                      { key: 'marksheetPG', label: 'PG Last Semester DMC' },
                    ].map(({ key, label }) => (
                      <div key={key} className="input-group">
                        <label>{label}</label>
                        <input type="file" onChange={(e) => {
                          const file = e.target.files[0];
                          handleNestedChange('documents', key, file);
                          if (file) setDocPreviews(prev => ({ ...prev, [key]: file.name }));
                        }}/>
                        {docPreviews[key] && (
                          <p style={{ fontSize: '11px', color: '#10b981', marginTop: '4px' }}>✓ {docPreviews[key]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-section half-width fee-section">
                  <h4>6. FEE DETAILS & INSTALLMENTS</h4>
                  <div className="input-group">
                    <label>Total Fee Amount (₹) *</label>
                    <input type="number" name="feeAmount" required value={formData.feeAmount} onChange={(e) => {
                      handleInputChange(e);
                      // If One Time is selected, auto-update its amount
                      if (formData.installmentPlan.planType === 'One Time') {
                        setFormData(prev => ({
                          ...prev,
                          feeAmount: e.target.value,
                          installmentPlan: {
                            ...prev.installmentPlan,
                            installments: [{ amount: e.target.value, dueDate: new Date().toISOString().split('T')[0] }]
                          }
                        }));
                      }
                    }} />
                    {formData.discountAmount > 0 && (
                      <p style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>
                        Discount applied: ₹{formData.discountAmount}
                      </p>
                    )}
                  </div>
                  
                  <div className="input-group" style={{marginTop: '15px'}}>
                    <label>Discount Coupon</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input 
                        type="text" 
                        value={couponInput} 
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder="Enter Code"
                        style={{ flex: 1, textTransform: 'uppercase' }}
                        disabled={!!formData.appliedCoupon}
                      />
                      {!formData.appliedCoupon ? (
                        <button type="button" onClick={handleApplyCoupon} style={{ padding: '8px 15px', backgroundColor: '#0284c7', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Apply</button>
                      ) : (
                        <button type="button" onClick={handleRemoveCoupon} style={{ padding: '8px 15px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Remove</button>
                      )}
                    </div>
                    {couponError && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '5px' }}>{couponError}</p>}
                    {couponSuccess && <p style={{ color: '#10b981', fontSize: '12px', marginTop: '5px' }}>{couponSuccess}</p>}
                  </div>

                  <div className="input-group" style={{marginTop: '15px'}}>
                    <label>Initial Amount Paid (₹)</label>
                    <input type="number" name="paidAmount" value={formData.paidAmount} onChange={handleInputChange} />
                  </div>

                  <div className="input-group" style={{marginTop: '15px'}}>
                    <label>Fee Structure</label>
                    <select name="planType" value={formData.installmentPlan.planType} onChange={handleInstallmentPlanChange}>
                      <option value="One Time">One Time</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Half Yearly">Half Yearly</option>
                      <option value="Yearly">Yearly</option>
                      <option value="Custom">Custom Installments</option>
                    </select>
                  </div>

                  {formData.installmentPlan.installments.length > 0 && (
                    <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '5px', border: '1px solid #e2e8f0' }}>
                      <p style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '10px' }}>Installment Schedule</p>
                      {formData.installmentPlan.installments.map((inst, index) => (
                        <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-end' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>Amount (₹)</label>
                            <input type="number" value={inst.amount} onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)} required style={{ width: '100%', padding: '5px' }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>Due Date</label>
                            <input type="date" value={inst.dueDate} onChange={(e) => handleInstallmentChange(index, 'dueDate', e.target.value)} required style={{ width: '100%', padding: '5px' }} />
                          </div>
                          {(formData.installmentPlan.planType === 'Custom' || formData.installmentPlan.planType === 'Monthly' || formData.installmentPlan.planType === 'Quarterly' || formData.installmentPlan.planType === 'Half Yearly' || formData.installmentPlan.planType === 'Yearly') && (
                            <button type="button" onClick={() => handleRemoveInstallment(index)} style={{ padding: '6px 10px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>X</button>
                          )}
                        </div>
                      ))}
                      {(formData.installmentPlan.planType === 'Custom' || formData.installmentPlan.planType === 'Monthly' || formData.installmentPlan.planType === 'Quarterly' || formData.installmentPlan.planType === 'Half Yearly' || formData.installmentPlan.planType === 'Yearly') && (
                        <button type="button" onClick={handleAddInstallment} style={{ marginTop: '5px', fontSize: '12px', padding: '5px 10px', backgroundColor: '#0ea5e9', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ Add Installment</button>
                      )}
                    </div>
                  )}
                  
                  <div className="submit-area">
                    <button type="submit" className="submit-btn registration-submit">{editMode ? 'Update Student' : 'Save Student Registration'}</button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Student Modal ── */}
      {viewStudent && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <span className="close-btn" onClick={() => setViewStudent(null)}>&times;</span>

            {/* Header with Logo + Profile Photo */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #0284c7', paddingBottom: '14px', marginBottom: '22px', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {/* Profile Photo */}
                <div style={{
                  width: '80px', height: '80px', borderRadius: '50%',
                  border: '3px solid #0284c7', overflow: 'hidden', flexShrink: 0,
                  background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {viewStudent.profileImage
                    ? <img src={`${BASE_URL}${viewStudent.profileImage}`} alt={viewStudent.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                      />
                    : <span style={{ fontSize: '28px', color: '#0284c7', fontWeight: 800 }}>
                        {viewStudent.name?.charAt(0)?.toUpperCase()}
                      </span>
                  }
                </div>
                {/* Name and ID */}
                <div>
                  <h3 style={{ color: '#0284c7', margin: 0, fontSize: '20px', fontWeight: 800 }}>{viewStudent.name}</h3>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>Enrollment ID: {viewStudent._id?.slice(-8).toUpperCase()}</p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>{viewStudent.course} {viewStudent.branch ? `· ${viewStudent.branch}` : ''}</p>
                </div>
              </div>
              <img src="/webmok-logo.png" alt="WebMok" style={{ height: '50px', objectFit: 'contain' }}
                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
            </div>

            {/* Fee Progress Bar */}
            <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px 18px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '8px', fontWeight: 600 }}>
                <span style={{ color: '#1e293b' }}>Fee Collection Progress</span>
                <span style={{ color: '#0284c7' }}>{Math.round(((viewStudent.paidAmount || 0) / (viewStudent.feeAmount || 1)) * 100)}%</span>
              </div>
              <div style={{ width: '100%', background: '#e2e8f0', borderRadius: '10px', height: '10px', overflow: 'hidden' }}>
                <div style={{ height: '100%', background: 'linear-gradient(90deg, #0ea5e9, #10b981)', width: `${Math.min(100, ((viewStudent.paidAmount || 0) / (viewStudent.feeAmount || 1)) * 100)}%`, transition: 'width 0.5s ease', borderRadius: '10px' }} />
              </div>
              <div style={{ display: 'flex', gap: '24px', marginTop: '10px', fontSize: '13px' }}>
                <span>Total: <strong>₹{(viewStudent.feeAmount || 0).toLocaleString('en-IN')}</strong></span>
                <span style={{ color: '#10b981' }}>Paid: <strong>₹{(viewStudent.paidAmount || 0).toLocaleString('en-IN')}</strong></span>
                <span style={{ color: '#ef4444' }}>Pending: <strong>₹{Math.max(0, (viewStudent.feeAmount || 0) - (viewStudent.paidAmount || 0)).toLocaleString('en-IN')}</strong></span>
              </div>
            </div>

            <div className="view-details-container">
              <div className="form-section">
                <h4>1. PERSONAL DETAILS</h4>
                <div className="details-grid">
                  <p><strong>Full Name:</strong> {viewStudent.name}</p>
                  <p><strong>Date of Birth:</strong> {viewStudent.dob || 'N/A'}</p>
                  <p><strong>Gender:</strong> {viewStudent.gender || 'N/A'}</p>
                  <p><strong>Father's Name:</strong> {viewStudent.fatherName || 'N/A'}</p>
                  <p><strong>Mother's Name:</strong> {viewStudent.motherName || 'N/A'}</p>
                  <p><strong>Guardian Phone:</strong> {viewStudent.guardianPhone || 'N/A'}</p>
                  <p><strong>Mobile:</strong> {viewStudent.phone || 'N/A'}</p>
                  <p><strong>Email:</strong> {viewStudent.email}</p>
                  <p><strong>Aadhar Number:</strong> {viewStudent.aadharNumber || 'N/A'}</p>
                  <p><strong>PAN Number:</strong> {viewStudent.panNumber || 'N/A'}</p>
                  {viewStudent.address && (
                    <p style={{ gridColumn: '1 / -1' }}>
                      <strong>Address:</strong> {[viewStudent.address.street, viewStudent.address.city, viewStudent.address.state, viewStudent.address.pincode].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>

              {/* 2. COURSE DETAILS */}
              <div className="form-section" style={{ marginTop: '16px' }}>
                <h4>2. COURSE DETAILS</h4>
                <div className="details-grid">
                  <p><strong>Course:</strong> {viewStudent.course}</p>
                  <p><strong>Duration:</strong> {viewStudent.duration || 'N/A'}</p>
                  <p><strong>Batch Timing:</strong> {viewStudent.batchTiming || 'N/A'}</p>
                  <p><strong>Branch:</strong> {viewStudent.branch || 'N/A'}</p>
                  <p><strong>Mode of Training:</strong> {viewStudent.modeOfTraining || 'N/A'}</p>
                  <p><strong>Preferred Batch:</strong> {viewStudent.preferredBatchTime || 'N/A'}</p>
                  <p><strong>Enrollment Date:</strong> {viewStudent.enrollmentDate ? new Date(viewStudent.enrollmentDate).toLocaleDateString('en-IN') : 'N/A'}</p>
                  <p><strong>Status:</strong>
                    <span className={viewStudent.status === 'Paid' ? 'paid' : viewStudent.status === 'Partially Paid' ? 'partially-paid' : 'pending'} style={{ marginLeft: '8px' }}>
                      {viewStudent.status}
                    </span>
                  </p>
                </div>
              </div>

              {/* 3. EDUCATIONAL DETAILS */}
              {viewStudent.educationDetails && viewStudent.educationDetails.filter(e => e.board || e.year || e.percentage).length > 0 && (
                <div className="form-section" style={{ marginTop: '16px' }}>
                  <h4>3. EDUCATIONAL DETAILS</h4>
                  <div className="table-responsive" style={{ marginTop: '10px' }}>
                    <table className="edu-table">
                      <thead>
                        <tr>
                          <th>Qualification</th>
                          <th>Board / University</th>
                          <th>Year</th>
                          <th>Percentage / CGPA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewStudent.educationDetails.filter(e => e.board || e.year || e.percentage).map((edu, idx) => (
                          <tr key={idx}>
                            <td>{edu.qualification}</td>
                            <td>{edu.board || '-'}</td>
                            <td>{edu.year || '-'}</td>
                            <td>{edu.percentage || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 4. WORK EXPERIENCE */}
              {/* {viewStudent.workExperience && (
                <div className="form-section" style={{ marginTop: '16px' }}>
                  <h4>4. WORK EXPERIENCE</h4>
                  <div className="details-grid">
                    <p><strong>Currently Working:</strong> {viewStudent.workExperience.isWorking ? 'Yes' : 'No'}</p>
                    {viewStudent.workExperience.isWorking && (
                      <>
                        <p><strong>Company:</strong> {viewStudent.workExperience.companyName || 'N/A'}</p>
                        <p><strong>Designation:</strong> {viewStudent.workExperience.designation || 'N/A'}</p>
                        <p><strong>Experience:</strong> {viewStudent.workExperience.experienceYears || 'N/A'} Years</p>
                      </>
                    )}
                  </div>
                </div>
              )} */}
              {/* 5. UPLOADED DOCUMENTS */}
              {viewStudent.documents && Object.values(viewStudent.documents).some(v => v && typeof v === 'string') && (
                <div className="form-section" style={{ marginTop: '16px' }}>
                  <h4>5. UPLOADED DOCUMENTS</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '10px' }}>
                    {viewStudent.documents.aadharCopy && typeof viewStudent.documents.aadharCopy === 'string' && (
                      <a href={`${BASE_URL}${viewStudent.documents.aadharCopy}`} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', background: '#e0f2fe', color: '#0369a1', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>📄 Aadhar Card</a>
                    )}
                    {viewStudent.documents.panCopy && typeof viewStudent.documents.panCopy === 'string' && (
                      <a href={`${BASE_URL}${viewStudent.documents.panCopy}`} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', background: '#e0f2fe', color: '#0369a1', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>📄 PAN Card</a>
                    )}
                    {viewStudent.documents.photos && typeof viewStudent.documents.photos === 'string' && (
                      <a href={`${BASE_URL}${viewStudent.documents.photos}`} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', background: '#e0f2fe', color: '#0369a1', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>🖼️ Photographs</a>
                    )}
                    {viewStudent.documents.marksheet10 && typeof viewStudent.documents.marksheet10 === 'string' && (
                      <a href={`${BASE_URL}${viewStudent.documents.marksheet10}`} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', background: '#e0f2fe', color: '#0369a1', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>📄 10th Marksheet</a>
                    )}
                    {viewStudent.documents.marksheet12 && typeof viewStudent.documents.marksheet12 === 'string' && (
                      <a href={`${BASE_URL}${viewStudent.documents.marksheet12}`} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', background: '#e0f2fe', color: '#0369a1', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>📄 12th Marksheet</a>
                    )}
                    {viewStudent.documents.marksheetUG && typeof viewStudent.documents.marksheetUG === 'string' && (
                      <a href={`${BASE_URL}${viewStudent.documents.marksheetUG}`} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', background: '#e0f2fe', color: '#0369a1', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>📄 UG Marksheet</a>
                    )}
                    {viewStudent.documents.marksheetPG && typeof viewStudent.documents.marksheetPG === 'string' && (
                      <a href={`${BASE_URL}${viewStudent.documents.marksheetPG}`} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', background: '#e0f2fe', color: '#0369a1', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>📄 PG Marksheet</a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="table-box">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Profile</th>
                <th>Name</th>
                <th>Course/Branch</th>
                <th>Contact</th>
                <th>Total Fee</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{textAlign: "center"}}>Loading...</td>
                </tr>
              ) : filteredStudents.length > 0 ? (
                filteredStudents.map((student) => {
                  const feeAmount = student.feeAmount || 0;
                  const paidAmount = student.paidAmount || 0;
                  const balance = feeAmount - paidAmount;
                  
                  return (
                    <tr key={student._id}>
                      <td>
                        {student.profileImage ? (
                          <img 
                            src={`${BASE_URL}${student.profileImage}`} 
                            alt={student.name} 
                            style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontWeight: 'bold' }}>
                            {student.name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </td>
                      <td>{student.name}</td>
                      <td>
                        {student.course}
                        {student.branch ? ` - ${student.branch}` : ''}
                      </td>
                      <td>
                        <div>{student.email}</div>
                        <div style={{fontSize: '12px', color: '#666'}}>{student.phone || 'N/A'}</div>
                      </td>
                      <td>₹{feeAmount.toLocaleString('en-IN')}</td>
                      <td style={{color: '#7AC943'}}>₹{paidAmount.toLocaleString('en-IN')}</td>
                      <td style={{color: balance > 0 ? '#ef4444' : '#64748b'}}>₹{balance > 0 ? balance.toLocaleString('en-IN') : 0}</td>
                      <td>
                        <span className={
                          student.status === 'Paid' ? 'paid' : 
                          student.status === 'Partially Paid' ? 'partially-paid' : 'pending'
                        }>
                          {student.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button onClick={() => setViewStudent(student)} className="view-btn" style={{ padding: '5px 10px', backgroundColor: '#e2e8f0', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', color: '#334155' }}>View</button>
                          <button onClick={() => handleEditClick(student)} className="view-btn" style={{ padding: '5px 10px', backgroundColor: '#0ea5e9', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', color: '#fff' }}>Edit</button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan="8" style={{textAlign: "center"}}>No students found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default StudentsView;
